import React from 'react';
import './OfferComparisonStrip.css';

// Inline strip rendered in SellerOffers' header. Lazy generation: the first
// time a primary party opens the Offers tab we show a Generate button; only
// after they click does ai-service get called. When the comparison is READY,
// the strip shows the narrative + View + Regenerate buttons. If the inputHash
// has drifted (new offer / new summary version), we tag the narrative as
// Outdated and nudge toward Regenerate.
//
// Button-always-visible policy (2026-10-29): the Generate action is shown
// whenever the feature is enabled for the viewer, even with <2 PA offers —
// the button is disabled with a help message explaining the minimum. Keeps
// the affordance discoverable instead of silently hidden behind a count.
const OfferComparisonStrip = ({ paOfferCount, offerComparison }) => {
  const {
    enabled, tooFew, loadError, comparison, isGenerating, generateError,
    open, generate,
  } = offerComparison;

  if (!enabled && !loadError) return null;

  const renderShell = (body) => (
    <div className="oc-strip">
      <div className="oc-strip-icon" aria-hidden="true">⚖</div>
      <div className="oc-strip-body">
        <p className="oc-strip-title">Offer Comparison</p>
        {body}
      </div>
    </div>
  );

  if (loadError) {
    return renderShell(
      <p className="oc-strip-error">
        Couldn't load the comparison.{' '}
        <button
          type="button"
          className="oc-strip-btn-outline oc-strip-btn"
          onClick={generate}
          disabled={isGenerating}
        >
          Try again
        </button>
      </p>,
    );
  }

  // Backend says: feature on, but fewer than 2 PA offers have READY summaries.
  // Show the Generate button in a disabled state so the control is always
  // discoverable; the help text explains the prerequisite.
  if (tooFew) {
    return renderShell(
      <>
        <p className="oc-strip-text oc-strip-text-muted">
          Generate AI summaries for at least two purchase-agreement offers and the comparison will appear here.
        </p>
        <div className="oc-strip-actions">
          <button
            type="button"
            className="oc-strip-btn"
            disabled
            title="Needs at least two purchase-agreement offers with AI summaries."
          >
            Generate Comparison
          </button>
        </div>
      </>,
    );
  }

  const status = comparison?.status;

  if (status === 'NOT_GENERATED') {
    return renderShell(
      <>
        <p className="oc-strip-text oc-strip-text-muted">
          Compare all {paOfferCount} purchase-agreement offers side-by-side — price, financing, contingencies, close date, and more. Neutral summary of the tradeoffs, no recommendation.
        </p>
        {generateError && <p className="oc-strip-error">{generateError}</p>}
        <div className="oc-strip-actions">
          <button
            type="button"
            className="oc-strip-btn"
            onClick={generate}
            disabled={isGenerating}
          >
            {isGenerating ? 'Starting…' : 'Generate Comparison'}
          </button>
        </div>
      </>,
    );
  }

  if (status === 'PENDING') {
    return renderShell(
      <p className="oc-strip-pending">Generating comparison summary…</p>,
    );
  }

  if (status === 'FAILED') {
    return renderShell(
      <>
        <p className="oc-strip-error">
          {comparison?.failureReason || 'Comparison generation failed.'}
        </p>
        <div className="oc-strip-actions">
          <button
            type="button"
            className="oc-strip-btn"
            onClick={generate}
            disabled={isGenerating}
          >
            {isGenerating ? 'Starting…' : 'Try again'}
          </button>
        </div>
      </>,
    );
  }

  if (status === 'READY') {
    const narrative = comparison?.narrative;
    const isStale = comparison?.isStale === true;
    return renderShell(
      <>
        {isStale && (
          <p className="oc-strip-stale">
            Offers have changed since this comparison was generated. Regenerate to refresh.
          </p>
        )}
        {narrative && <p className="oc-strip-text">{narrative}</p>}
        <div className="oc-strip-actions">
          <button type="button" className="oc-strip-btn" onClick={open}>
            View full comparison
          </button>
          <button
            type="button"
            className="oc-strip-btn-outline oc-strip-btn"
            onClick={generate}
            disabled={isGenerating}
          >
            {isGenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </>,
    );
  }

  return null;
};

export default OfferComparisonStrip;
